/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2019, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore'
],
function(_) {
	'use strict';

	class FilterSpecHelper {
		/**
		 * Used for initializing and finding the appropriate id for a filter entry
		 * from the other field values that determine the id.
		 * @param columnIdOrDataPointId	a columnId or, for dataPoints, the baseId for the dataPoint (usu a stringified array of the items that the point is based on.)
		 * @param aggregationType the aggregation type (ordinals only)
		 * @param idSuffix - appended to the base id to allow keyed access on multiple keep/exclude filters on the same item.
		 */
		static getFilterId (columnId, aggregation, idSuffix) {
			if (idSuffix > 0) {
				//For keepexclude (and potentially other actions), allow multiple filter entries on the same item.
				return columnId + '_' + idSuffix;
			} else {
				return (aggregation) ? columnId + '_' + aggregation : columnId;
			}
		}

		/**
         * This function returns true if the values of a filterEntry (simple or compound contain a number.)
         * For compound filters, this function recursively searches all of the branches.
         * @returns true if the valueset does.
         */
		static containsNumber (filter) {
			if (this.isCompound(filter)) {
				return !!_.find(filter.values, (valueObject) => {
					//Each value in the values collection of a compound entry is another filterEntry.
					return this.containsNumber(valueObject);
				});
			}
			//At the leaf, the values collection contains an array of FilterValue objects (each having an id and value)
			return !!(filter.values && _.find(filter.values, (value) => {
				const filterValue = _.isObject(value) ? value.d : value;
				return _.isNumber(filterValue);
			}));
		}

		/**
		 * Returns true if the filter entry has no meaningful content or values.
		 * @param {object} filterEntry
		 * @returns {boolean}
		 */
		static isEmpty (filter) {
			return !filter.valueDataItem &&
				!filter.conditions &&
				filter.values && !filter.values.length &&
				filter.operator !== 'notin';
		}

		static isOrdinal (filter) {
			return !!filter.aggregationType;
		}
		/**
		 * A compound filter is a filterEntry where a single object does not completely define the filter on its own but is part of a
		 * tree structure with other filterEntry instances.  A compound filter is needed to do dataPoint filtering because
		 * 1) multiple 'in' values (each defined as a filterEntry) must be 'anded' together to define a point and
		 * 2) multiple points must be 'ored' together to produce a set of points.
		 * 3) the set of points must be 'not' ed to produce a negative data point filter.
		 * @returns true if this filterEntry is the root of a compound() filter tree (or sub-tree)
		 */
		static isCompound (filter) {
			const compoundOps = ['and', 'or', 'not'];
			return compoundOps.indexOf(filter.operator) > -1;
		}

		/**
		 * @returns true if this filter entry is a numeric range filter
		 */
		static rangeOps = ['between', 'notbetween', 'lt', 'gt'];
		static isRange (filter) {
			return FilterSpecHelper.rangeOps.indexOf(filter.operator) > -1;
		}

		/**
		 * @returns true if this filter entry is a model filter
		 */
		static isModel (filter) {
			return !filter.operator && !filter.value && !filter.values;
		}

		/**
		 * @returns true if this is an 'additional filter' (ie: a second/third filter on a given item or tuple)
		 */
		static isAdditional(filter) {
			return (filter.idSuffix) ? true : false;
		}

		static setBinningItemId (filter, querySpec) {
			if (filter.filterBins) {
				delete querySpec.itemId;
				querySpec.dataItem = filter.id;
			}
		}

		/**
         * @returns the set of columnId's nested inside this compound filter.
         *
         * Example:
         *	{ or:	{ and:	{ columnId: '1', operator: 'in', values: ['a', 'b', 'c'] },
         *					{ columnId: '5', operator: 'in', values: ['d'] }
         *          },
         *          { and:	{ columnId: '5', operator: 'in', values: ['e'] }
         *					{ columnId: '3', operator: 'in', values: ['f'] }
         *          }
         *	}
         *	would return ['1','3','5']
         */
		static getSelectedCompoundFilterColumnIds (filters) {
			const selectedColumnIdMap = {};
			_.each(filters, (filter) => {
				if (this.isCompound(filter)) {
					this._loadSelectedColumnIdMap(selectedColumnIdMap, filter);
				}
			});
			return _.keys(selectedColumnIdMap);
		}

		/**
		 * @param {boolean} electionFiltersOnly if true only collect the column ids from filter with visualizationId
         * @returns the set of columnIds for simple selections (ones where the values are not more filter entries)....see compound above.
         */
		static getSelectedSimpleFilterColumnIds (filters, selectionFiltersOnly) {
			const selectedColumnIds = [];
			_.each(filters, (filter) => {
				if (!this.isCompound(filter) && filter.columnId !== undefined &&
					selectedColumnIds.indexOf(filter.columnId) === -1 &&
					(selectionFiltersOnly === undefined || filter.visualizationId !== undefined)) {
					selectedColumnIds.push(filter.columnId);
				}
			});
			return selectedColumnIds;
		}

		/**
		 * recursively build up a selectedColumnId map as a map of form.
		 * This is used to quickly build a unique map of all of the columnId's in a compound filter.
		 * {
		 *		idN: 'idN'
		 * }
		 */
		static _loadSelectedColumnIdMap (selectedColumnIdMap, filter) {
			if (filter.columnId !== undefined && filter.visualizationId !== undefined) {
				selectedColumnIdMap[filter.columnId] = filter.columnId;
			}
			_.each(filter.values, (value) => {
				this._loadSelectedColumnIdMap(selectedColumnIdMap, value);
			});
		}
	}

	return FilterSpecHelper;
});